import { useGetCentralBranding, useGetSettingsDesign } from '@agos/api-client/platform'
import { useEffect } from 'react'

import { applied, brandingOf, schemeOf } from './branding'

/**
 * Puts the franchise's own look on the document.
 *
 * The rules are in `branding.ts` and are tested without a browser; this is the four lines of DOM that
 * carry them out, and it renders nothing. It mounts inside the query provider and **outside** the branch
 * gate, because a visitor still choosing a branch is already looking at the wrong colours if this waits.
 *
 * **Why a component and not a call in `main.tsx`:** the branding is a query, and it is re-fetched when
 * the tab regains focus (a super-admin changing a franchise's tier or theme should not need the customer
 * to hard-reload). That is TanStack's `refetchOnWindowFocus`, which this app turns off globally for good
 * reason — a menu must not move under a reading thumb — so it is turned back on here, for this one query.
 *
 * Everything set here is set on `<html>` rather than swapped in the stylesheet, so it survives a theme
 * sheet loading later and can be inspected in devtools as what it is: this franchise's overrides.
 *
 * **And it is the only thing that touches `<html>` - §9.330.** The light/dark scheme is a second,
 * independent axis from the theme id, from a different endpoint, and it was added here rather than in
 * a sibling component for one reason: two components writing attributes on the same element is the
 * fault §9.328 had just finished removing from the notices. One owner of `<html>`, two queries.
 */
export function BrandingApplier() {
  const { data } = useGetCentralBranding(undefined, {
    query: {
      refetchOnWindowFocus: true,
      // A restaurant's colours are not news. This is long enough that no page transition refetches it,
      // and focus still picks up a change made in another tab.
      staleTime: 5 * 60_000,
    },
  })

  // §9.330 - the franchise's own light/dark choice, which is a different endpoint from the branding
  // above and a different axis. Same caching for the same reason: a scheme is not news, and a
  // franchise switching it while a customer reads the menu should land on the next focus.
  const { data: design } = useGetSettingsDesign({
    query: { refetchOnWindowFocus: true, staleTime: 5 * 60_000 },
  })

  useEffect(() => {
    if (typeof document === 'undefined' || design === undefined) return
    const root = document.documentElement
    const scheme = schemeOf(design)
    // Only ever *set* to `dark`: the light palette is `:root`'s own, so removing the attribute is
    // what light means.
    //
    // **And it removes, which makes the server the authority.** `index.html` pre-applies the
    // attribute from a cache before the bundle runs, and its own comment says removing it on a miss
    // *"would fight the app, which owns the attribute from its first render onward"*. That was
    // written for an owner that did not exist - nothing in this app read the setting - so a stale
    // cache saying dark outlived a franchise switching back to light, for every returning visitor.
    // This is that owner.
    //
    // Nothing happens while the query is in flight: the attribute the bootstrap painted stays until
    // the franchise's own answer arrives, which is the whole point of the pre-paint. `undefined` is
    // not *light*, it is *not yet known*.
    if (scheme === 'dark') root.setAttribute('data-scheme', 'dark')
    else root.removeAttribute('data-scheme')

    // **The other half of the pre-paint, and it had no writer - §9.330.** The bootstrap reads
    // `fastkitchen_theme` from `localStorage`, and that key is written by `ThemeProvider`, which runs
    // in the **manager** - a different origin, so on the customer site it was never there and the
    // bootstrap could never fire. Caching the answer here is what makes the second visit paint dark
    // in the first frame instead of flashing white.
    //
    // Merged rather than written, and wrapped: the key is shared with whatever else may cache under
    // it, and `localStorage` throws outright in a browser set to block site data.
    try {
      const raw = localStorage.getItem('fastkitchen_theme')
      const cached = raw === null ? {} : (JSON.parse(raw) as Record<string, unknown>)
      if (cached.colorScheme !== scheme) {
        localStorage.setItem('fastkitchen_theme', JSON.stringify({ ...cached, colorScheme: scheme }))
      }
    } catch {
      // The attribute above is already set; the cache is only next time's first frame.
    }
  }, [design])

  useEffect(() => {
    const branding = brandingOf(data)
    if (branding === null || typeof document === 'undefined') return

    const look = applied(branding)
    const root = document.documentElement
    for (const [name, value] of Object.entries(look.variables)) root.style.setProperty(name, value)

    // `classic` removes the attribute rather than setting it — the default palette is `:root`'s, and
    // `[data-theme="classic"]` selects nothing.
    if (look.dataTheme === null) root.removeAttribute('data-theme')
    else root.setAttribute('data-theme', look.dataTheme)

    if (look.documentTitle !== null) document.title = look.documentTitle
    if (look.faviconUrl !== null) linkHref('icon', look.faviconUrl)
    // iOS ignores the manifest, so the home-screen name and the status-bar colour come from these two
    // meta tags. Android reads `/api/manifest.webmanifest`, which the backend already resolves from the
    // Host header before any JS runs.
    metaContent('apple-mobile-web-app-title', look.appleTitle)
    metaContent('theme-color', look.themeColor)
    // The home-screen icon, likewise: iOS reads this link at *Add to Home Screen* time, which is after
    // this has run. A static file here would put the first franchise's logo on every other franchise's
    // home screen — the same reason `index.html` links no static icons.
    if (look.faviconUrl !== null) linkHref('apple-touch-icon', look.faviconUrl)
  }, [data])

  return null
}

function metaContent(name: string, content: string): void {
  let tag: HTMLMetaElement | null = document.querySelector(`meta[name='${name}']`)
  if (tag === null) {
    tag = document.createElement('meta')
    tag.name = name
    document.head.appendChild(tag)
  }
  tag.content = content
}

function linkHref(rel: string, href: string): void {
  let tag: HTMLLinkElement | null = document.querySelector(`link[rel='${rel}']`)
  if (tag === null) {
    tag = document.createElement('link')
    tag.rel = rel
    document.head.appendChild(tag)
  }
  tag.href = href
}
